import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import Button from '@components/atoms/button/button';
import { useNegotiation } from '@context/negotiation.context';
import { useTranslation } from 'next-i18next';
import sanitizer from '@helpers/sanitizer';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { NegotiationsService } from '@services/negotiations-service';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Spinner from '@components/atoms/spinner/spinner';
import logger from '@helpers/utils/logger/client';
import styles from './negotiation-fpd.module.scss';

export interface MMAOFlashPriceDropProps {
  showFpdSection: boolean;
  trackingFrom?: string;
}

const NegotiationFlashPriceDrop: React.FC<MMAOFlashPriceDropProps> = (props) => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { showFpdSection, trackingFrom } = props;
  const { negotiation: contextNegotiation, setNegotiation } = useNegotiation();
  // showFpd=true && !isActive && isEligible - Bottom sheet 1
  const [showBottomSheetOne, setShowBottomSheetOne] = useState<boolean>(
    showFpdSection && !contextNegotiation?.flashPriceDrop?.isActive && contextNegotiation?.flashPriceDrop?.isEligible,
  );

  // local state for show the confirm screen - Bottom sheet 2
  const [showBottomSheetConfirm, setShowBottomSheetConfirm] = useState<boolean>(false);

  // isActive - Activated Bottom Sheet with see more/less toggle
  const [showActivatedBottomSheet, setShowActivatedBottomSheet] = useState<boolean>(
    contextNegotiation?.flashPriceDrop?.isActive,
  );

  // showFpd=true && !isActive && isEligible && !showBottomSheetOne with learn more link
  const [showDiscover, setShowDiscover] = useState<boolean>(
    showFpdSection && showBottomSheetOne !== undefined && !showBottomSheetOne && !showActivatedBottomSheet,
  );

  // local state for toggle more info on the activated screen
  const [showFpdMoreInfo, setShowFdpMoreInfo] = useState<boolean>(false);

  // Activation loading
  const [activationLoading, setActivationLoading] = useState<boolean>(false);

  const { sendAnalyticEvent: sendNegotiationAnalyticEvents } = useAnalyticEvents('mmao');

  const sendAnEvent = (eventName: string) => {
    sendNegotiationAnalyticEvents(eventName, {
      eventValue: contextNegotiation?.product?.id,
      eventProperty: contextNegotiation?.id,
    });
  };

  const finalDate = useMemo<string>(() => {
    const expiryMonth = new Date(contextNegotiation?.flashPriceDrop.expiryDate).toLocaleString('default', {
      month: 'short',
    });
    const expiryDate = new Date(contextNegotiation?.flashPriceDrop.expiryDate).getDate();

    const formatAMPM = (date) => {
      let hours = date.getHours();
      let minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      minutes = minutes < 10 ? '0' + minutes : minutes;
      return hours + ':' + minutes + ' ' + ampm;
    };

    return `${expiryMonth} ${expiryDate}, ${formatAMPM(new Date(contextNegotiation?.flashPriceDrop?.expiryDate))}`;
  }, [contextNegotiation]);

  const resetFDPVariables = () => {
    setShowActivatedBottomSheet(false);
    setShowBottomSheetConfirm(false);
    setShowBottomSheetOne(false);
    setShowDiscover(false);
    setActivationLoading(false);
  };

  const confirmActivation = async (): Promise<void> => {
    try {
      setActivationLoading(true);
      await NegotiationsService.executeFPD(contextNegotiation?.id, {});
      const latestNegotiation: Negotiation = await NegotiationsService.getNegotiation(contextNegotiation?.id, true);
      setNegotiation(latestNegotiation);
      resetFDPVariables();
      setShowActivatedBottomSheet(true);
    } catch (error) {
      logger.error(error, 'Error occurred at action: excute FPD on mmao negotiation');
    }
  };

  useEffect(() => {
    if (showBottomSheetOne) {
      sendAnEvent('mmao_fpd_popin_view');
    }
  }, [showBottomSheetOne]);

  useEffect(() => {
    if (showFpdSection && trackingFrom === 'mmao_ongoing_banner_notification') {
      sendAnEvent('mmao_fpd_notification');
    }
  }, [trackingFrom]);

  return (
    <div className={clsx(styles.mmao__fpd, 'vc-text-m')}>
      {showDiscover && (
        <div className={styles.mmao__fpd__discover}>
          {t('SELL.FLASH_PRICE_DROP_BOTTOM_BANNER_2.DESCRIPTION')}
          <Button
            disableDefaultStyling={true}
            className={styles.mmao__fpd__discover__button}
            onClick={() => {
              resetFDPVariables();
              sendAnEvent('mmao_fpd_discover');
              setShowBottomSheetOne(true);
            }}
          >
            {t('SELL.FLASH_PRICE_DROP_BOTTOM_BANNER_2.CTA')}
          </Button>
        </div>
      )}
      {showBottomSheetOne && (
        <div className={styles.mmao__fpd__activate}>
          <b>
            <h3>{t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.CHAPO_1')}</h3>
          </b>
          <p>
            {!contextNegotiation?.flashPriceDrop?.sellerEarning ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.CHAPO_2_A', {
                      price: contextNegotiation?.flashPriceDrop?.price,
                    }),
                  ),
                }}
              />
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.CHAPO_2_B', {
                      price: contextNegotiation?.flashPriceDrop?.price,
                      sellerEarning: contextNegotiation?.flashPriceDrop?.sellerEarning,
                    }),
                  ),
                }}
              />
            )}
          </p>
          <div className={styles.mmao__fpd__activate__subText}>
            <p>{t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.DESCRIPTION')}</p>
          </div>
          <div className={styles.mmao__fpd__activate__buttons}>
            <Button
              className={styles.mmao__fpd__activate__buttons__optOut}
              variant="secondary"
              fullWidth={true}
              size="small"
              onClick={() => {
                resetFDPVariables();
                setShowDiscover(true);
                sendAnEvent('mmao_fpd_opt_out');
              }}
            >
              {t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.CTA_2')}
            </Button>
            <Button
              className={styles.mmao__fpd__activate__buttons__optIn}
              variant="primary"
              fullWidth={true}
              size="small"
              onClick={() => {
                resetFDPVariables();
                setShowBottomSheetConfirm(true);
                sendAnEvent('mmao_fpd_opt_in');
              }}
            >
              {t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_1.CTA_1')}
            </Button>
          </div>
        </div>
      )}
      {showBottomSheetConfirm && (
        <div className={styles.mmao__fpd__activateConfirm}>
          <h3>
            {!contextNegotiation?.flashPriceDrop?.sellerEarning ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.CHAPO_A', {
                      price: contextNegotiation?.flashPriceDrop?.price,
                    }),
                  ),
                }}
              />
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.CHAPO_B', {
                      price: contextNegotiation?.flashPriceDrop?.price,
                      sellerEarning: contextNegotiation?.flashPriceDrop?.sellerEarning,
                    }),
                  ),
                }}
              />
            )}
          </h3>
          <div>
            <p>{t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.DESCRIPTION')}</p>
          </div>
          <div className={styles.mmao__fpd__activate__buttons}>
            <Button
              className={styles.mmao__fpd__activate__buttons__optOut}
              variant="secondary"
              size="small"
              onClick={() => {
                resetFDPVariables();
                setShowDiscover(true);
                sendAnEvent('mmao_fpd_opt_in_reject');
              }}
            >
              {t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.CTA_2')}
            </Button>
            <Button
              className={styles.mmao__fpd__activate__buttons__optIn}
              disabled={activationLoading}
              variant="primary"
              size="small"
              fullWidth={true}
              onClick={() => {
                confirmActivation();
                sendAnEvent('mmao_fpd_opt_in_confirm');
              }}
            >
              {activationLoading ? (
                <Spinner className={styles.mmao__fpd__activate__buttons__loading} />
              ) : (
                <>{t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.CTA_1')}</>
              )}
            </Button>
          </div>
        </div>
      )}
      {showActivatedBottomSheet && (
        <div className={styles.mmao__fpd__activated}>
          <p>
            {t('SELL.FLASH_PRICE_DROP_BOTTOM_BANNER_1.DESCRIPTION', {
              date: finalDate,
            })}
          </p>
          <Button
            disableDefaultStyling={true}
            className={styles.mmao__fpd__activated__button}
            onClick={() => {
              setShowFdpMoreInfo(!showFpdMoreInfo);
              if (!showFpdMoreInfo) {
                sendAnEvent('mmao_fpd_learn_more');
              }
            }}
          >
            <span className={styles.mmao__fpd__activated__button__icon}>
              {!showFpdMoreInfo
                ? t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_3.CTA_1')
                : t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_3.CTA_2')}
              <Icon
                className={styles.icon}
                name={`${!showFpdMoreInfo ? 'arrow-head-down' : 'arrow-head-up'} `}
                ariaLabel={!showFpdMoreInfo ? tA11y('A11Y.ICON.DROPDOWN_DOWN') : tA11y('A11Y.ICON.DROPDOWN_UP')}
              />
            </span>
          </Button>
          {showFpdMoreInfo && (
            <div className={styles.mmao__fpd__activated__moreToggle}>
              <b>
                <h3>{t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_2.TITLE')}</h3>
              </b>
              <p>
                {!contextNegotiation?.flashPriceDrop?.sellerEarning ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizer(
                        t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_3.DESCRIPTION_1_A', {
                          price: contextNegotiation?.flashPriceDrop?.price,
                        }),
                      ),
                    }}
                  />
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizer(
                        t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_3.DESCRIPTION_1_B', {
                          price: contextNegotiation?.flashPriceDrop?.price,
                          sellerEarning: contextNegotiation?.flashPriceDrop?.sellerEarning,
                        }),
                      ),
                    }}
                  />
                )}
              </p>
              <div>
                <p>
                  {t('SELL.FLASH_PRICE_DROP_BOTTOM_SHEET_3.DESCRIPTION_2', {
                    date: finalDate,
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NegotiationFlashPriceDrop;
